import { useCallback, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { CreditCard, Check, X, Sparkle, Lightning, Crown, ChartLineUp, RocketLaunch } from '@phosphor-icons/react';
import StrategistTrialCard from './StrategistTrialCard';

const PLAN_META = {
  free: {
    id: 'free',
    amount: 0,
    icon: Sparkle,
  },
  creator: {
    id: 'creator',
    amount: 25,
    icon: Lightning,
  },
  strategist: {
    id: 'strategist',
    amount: 49,
    icon: Crown,
  },
};

const PLAN_IDS = ['free', 'creator', 'strategist'];

const COMPARISON = [
  { key: 'projects', free: '1', creator: '3', strategist: 'unlimited' },
  { key: 'contents', free: '7', creator: '50', strategist: 'unlimited' },
  { key: 'publishing', free: true, creator: true, strategist: true },
  { key: 'rss', free: false, creator: false, strategist: true },
  { key: 'analytics', free: false, creator: false, strategist: true },
  { key: 'imageEngine', free: 'base', creator: 'base', strategist: 'gptPremium' },
  // Free has no recurring monthly allowance, but it does receive 250
  // one-time starter credits. Paid plans renew their allowance monthly.
  { key: 'aiCredits', free: 'credits250', creator: 'credits1000', strategist: 'credits3000' },
];

function FeatureRow({ children, available = true }) {
  const Icon = available ? Check : X;
  return (
    <li className={`flex items-start gap-2 text-sm ${available ? 'text-[var(--text-secondary)]' : 'text-[var(--text-muted)] opacity-70'}`}>
      <Icon size={16} className="mt-0.5 flex-shrink-0" color={available ? 'var(--accent-green)' : '#64748b'} />
      <span>{children}</span>
    </li>
  );
}

function ComparisonValue({ value, t }) {
  if (typeof value === 'boolean') {
    return value
      ? <Check size={17} weight="bold" color="var(--accent-green)" className="mx-auto" />
      : <X size={17} weight="bold" color="#64748b" className="mx-auto" />;
  }
  return <span>{t(`billing.comparison.values.${value}`, value)}</span>;
}

export default function Billing() {
  const { api, user, effectivePlan } = useAuth();
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 5) return;
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === 'paid') return;
    } catch {}
    setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
  }, [api]);

  useEffect(() => {
    api.get('/billing/plans').then(r => setPlans(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      pollStatus(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [api, pollStatus]);

  // BILLING-IDENTITY-SEPARATION-001 (task section 10): always the
  // authenticated upgrade endpoint — it rejects admin accounts, ties the
  // resulting transaction immutably to the caller's own user id via
  // server-set Stripe metadata, and never trusts the Checkout email.
  const subscribe = async (planId) => {
    setLoading(planId);
    try {
      const origin = window.location.origin;
      const { data } = await api.post('/billing/upgrade-checkout', { plan_id: planId, origin_url: origin, ...(billingPeriod === 'annual' ? { billing_period: 'annual' } : {}) });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      alert(t('billing.checkoutError') + ': ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(null);
    }
  };

  const cards = PLAN_IDS.map((planId) => {
    const remotePlan = plans.find((plan) => plan.id === planId) || {};
    return {
      ...PLAN_META[planId],
      name: t(`billing.plans.${planId}.name`),
      eyebrow: t(`billing.plans.${planId}.eyebrow`),
      description: t(`billing.plans.${planId}.description`),
      bestFor: t(`billing.plans.${planId}.bestFor`),
      included: t(`billing.plans.${planId}.included`, { returnObjects: true }),
      locked: t(`billing.plans.${planId}.locked`, { returnObjects: true }),
      amount: remotePlan.amount ?? PLAN_META[planId].amount,
    };
  });

  return (
    <div className="w-full max-w-7xl mx-auto">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)' }}>
          <ChartLineUp size={14} weight="fill" color="var(--gradient-start)" />
          {t('billing.heroEyebrow')}
        </div>
        <h1 className="text-3xl md:text-4xl font-bold gradient-text mb-2" data-testid="billing-title">{t('billing.titleFull')}</h1>
        <p className="text-[var(--text-secondary)] text-sm max-w-2xl mx-auto">
          {t('billing.heroSubtitle')}
        </p>
      </div>

      <StrategistTrialCard />
      <div className="flex justify-center gap-3 mb-8" role="group" aria-label="Billing period">
        <button type="button" aria-pressed={billingPeriod === 'monthly'} onClick={() => setBillingPeriod('monthly')} className={billingPeriod === 'monthly' ? 'btn-gradient' : 'btn-ghost'}>{t('billing.periodMonthly', 'Mensile')}</button>
        <button type="button" aria-pressed={billingPeriod === 'annual'} onClick={() => setBillingPeriod('annual')} className={billingPeriod === 'annual' ? 'btn-gradient' : 'btn-ghost'}>{t('billing.periodAnnual', 'Annuale · 2 mesi gratis')}</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 xl:gap-6 mb-8">
        {cards.map((plan, i) => {
          const PlanIcon = plan.icon || Lightning;
          // EFFECTIVE-PLAN-SESSION-ONBOARDING-001: uses the EFFECTIVE
          // plan, not the raw base plan — a user on a temporary Power
          // User Creator/Strategist grant must see that plan as current
          // (and not be offered a redundant checkout for it), even
          // though their stored `user.plan` is still Free.
          const resolvedPlan = effectivePlan || user?.plan;
          const isCurrent = (resolvedPlan === plan.id && (plan.id === 'free' || (user?.billing_period || 'monthly') === billingPeriod)) || (!resolvedPlan && plan.id === 'free');
          const isPopular = plan.id === 'strategist';
          const isFree = plan.id === 'free';

          return (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="card relative flex flex-col"
              style={isPopular ? { border: '2px solid var(--gradient-start)', background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(236,72,153,0.08) 100%)' } : {}}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="badge purple text-[10px]">{t('billing.bestUpgrade')}</span>
                </div>
              )}

              <div className="mb-5">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] mb-2">{plan.eyebrow}</p>
                    <h3 className="text-2xl font-bold">{plan.name}</h3>
                  </div>
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: isPopular ? 'linear-gradient(135deg, var(--gradient-start), var(--gradient-end))' : 'var(--bg-secondary)' }}>
                    <PlanIcon size={24} weight="fill" style={{ color: isPopular ? '#fff' : 'var(--gradient-start)' }} />
                  </div>
                </div>
                <p className="text-sm text-[var(--text-secondary)] min-h-[42px]">{plan.description}</p>
                <div className="mt-5 flex items-end gap-1">
                  <span className="text-4xl font-bold">€{billingPeriod === "annual" && !isFree ? plan.amount * 10 : plan.amount}</span>
                  <span className="text-[var(--text-muted)] mb-1">{billingPeriod === "annual" && !isFree ? t("billing.perYear", "/anno") : t("billing.perMonth")}</span>
                </div>
                {billingPeriod === 'annual' && !isFree && <p className="text-xs text-emerald-400 mt-2">{t('billing.billedAnnually', 'Fatturato annualmente')}{plan.id === 'creator' ? ' · 5 progetti attivi' : ''}</p>}
                <p className="text-xs text-[var(--text-muted)] mt-2">{t('billing.idealFor')}: <span className="text-[var(--text-secondary)]">{plan.bestFor}</span></p>
              </div>

              <div className="space-y-5 flex-1">
                <div>
                  <p className="text-xs font-semibold uppercase text-[var(--text-muted)] mb-3">{t('billing.canDo')}</p>
                  <ul className="space-y-2.5">
                    {plan.included.map((feature) => <FeatureRow key={feature}>{feature}</FeatureRow>)}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-[var(--text-muted)] mb-3">{t('billing.notIncluded')}</p>
                  <ul className="space-y-2.5">
                    {plan.locked.map((feature) => <FeatureRow key={feature} available={false}>{feature}</FeatureRow>)}
                  </ul>
                </div>
              </div>

              <button
                data-testid={`subscribe-${plan.id}-btn`}
                className={`${isCurrent || isFree ? 'btn-ghost' : 'btn-gradient'} w-full mt-6 flex items-center justify-center gap-2`}
                onClick={() => !isCurrent && !isFree && subscribe(plan.id)}
                disabled={isCurrent || isFree || loading === plan.id}
              >
                {!isFree && !isCurrent && <CreditCard size={15} />}
                {loading === plan.id ? t('billing.redirecting') : isCurrent ? t('billing.currentPlan') : isFree ? t('billing.freePlan') : t('billing.activatePlan', { plan: plan.name })}
              </button>
            </motion.div>
          );
        })}
      </div>

      <div className="card mb-8 flex flex-col gap-2">
        <h3 className="font-bold">Agency</h3>
        <p className="text-sm text-[var(--text-secondary)]">{t('billing.agencyDescription', 'Il tuo brand, la potenza di Sketchario. White Label e flussi per agenzie su preventivo.')}</p>
        <a href="/contact.html" className="btn-gradient self-start">{t('billing.agencyDemo', 'Richiedi una demo')}</a>
      </div>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="card overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <RocketLaunch size={18} weight="fill" color="var(--gradient-start)" />
              {t('billing.comparison.title')}
            </h3>
            <p className="text-xs text-[var(--text-muted)]">{t('billing.comparison.subtitle')}</p>
          </div>
          <span className="text-[11px] text-[var(--text-muted)]">{t('billing.comparison.note')}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr className="text-left text-[var(--text-muted)] border-b border-[var(--border-color)]">
                <th className="py-3 font-semibold">{t('billing.comparison.feature')}</th>
                <th className="py-3 font-semibold text-center">{t('billing.plans.free.name')}</th>
                <th className="py-3 font-semibold text-center">{t('billing.comparison.creatorHeader')}</th>
                <th className="py-3 font-semibold text-center">{t('billing.comparison.strategistHeader')}</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row) => (
                <tr key={row.key} className="border-b border-[var(--border-color)] last:border-0">
                  <td className="py-3 text-[var(--text-secondary)]">{t(`billing.comparison.rows.${row.key}`)}</td>
                  <td className="py-3 text-center text-[var(--text-muted)]"><ComparisonValue value={row.free} t={t} /></td>
                  <td className="py-3 text-center"><ComparisonValue value={row.creator} t={t} /></td>
                  <td className="py-3 text-center font-semibold"><ComparisonValue value={row.strategist} t={t} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
